import { useContext } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { httpClient } from 'src/utils/HttpClients';
import { AppContext } from 'src/context';
import type { TEditNotice, TMeta, TNotice } from 'src/types';
import omit from 'lodash/omit';
import get from 'lodash/get';
import findIndex from 'lodash/findIndex';
import { getServerError } from 'src/utils/helpers/errors';

type TInfiniteNotices = {
    pageParams: unknown[];
    pages: { data: TNotice[]; metadata: TMeta }[];
};

export const useEditNotice = (noticeId?: string) => {
    const queryClient = useQueryClient();
    const {
        actions: { handleCallNotification },
    } = useContext(AppContext);

    const mutationFn = async (payload: TEditNotice) => {
        const response = await httpClient.put(
            `/notices/${noticeId}`,
            omit(payload, ['location_name'])
        );
        return get(response, 'data');
    };

    const onMutate = (payload: TEditNotice) => {
        const prevQuery = queryClient.getQueryData<TInfiniteNotices>(['notices']);

        queryClient.setQueryData<TInfiniteNotices | undefined>(['notices'], (oldQuery) => {
            const prevPages = get(oldQuery, 'pages', [] as TInfiniteNotices['pages']);
            const pages = prevPages.reduce((acc, item) => {
                const i = findIndex(item.data ?? [], (notice) => notice.id === noticeId);
                if (i >= 0) {
                    const nextPage = {
                        ...item,
                        data: [
                            ...item.data.slice(0, i),
                            { ...item.data[i], ...payload },
                            ...item.data.slice(i + 1),
                        ],
                    };
                    return [...acc, nextPage];
                }
                return [...acc, item];
            }, [] as TInfiniteNotices['pages']);
            return oldQuery ? { ...oldQuery, pages } : undefined;
        });

        return () => queryClient.setQueryData<TInfiniteNotices | undefined>(['notices'], prevQuery);
    };

    const onSuccess = async () => {
        await queryClient.invalidateQueries(['notices']);
        handleCallNotification({ message: 'The notice has been updated successfully' });
    };

    const onError = (error: unknown) => {
        handleCallNotification({ message: getServerError(error), type: 'danger' });
    };

    return useMutation(mutationFn, {
        onMutate,
        onSuccess,
        onError,
    });
};
